// ArtifactViewer — viewer FULLSCREEN multi-extensão de artefatos (DEM-2037).
//
// Aba Artefatos do DemandDetailPanel: ao clicar num artefato abre este viewer
// em tela cheia (overlay fixed inset-0). Requisitos do AC#3:
//   - renderiza QUALQUER extensão: imagem (img), pdf/html (iframe), md
//     (MarkdownViewer), txt/json (pre), fallback (link + download);
//   - FECHAR → volta ao modal de detalhe preservando a aba ativa (o caller
//     controla isso — o viewer só dispara onClose, sem desmontar a modal);
//   - NAVEGAR entre artefatos SEM fechar (← →, índice atual/total);
//   - DOWNLOAD por extensão real (atributo download com nome derivado).
//
// Texto (md/txt/json) é buscado client-side via fetch da signed_url; demais
// extensões usam a própria URL no elemento nativo. Sem URL → estado claro.

import { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ExternalLink, FileWarning } from 'lucide-react';
import type { DemandArtifact } from '@/lib/api';
import { MarkdownViewer } from '@/components/raios/MarkdownViewer';
import { cn } from '@/lib/utils';
import { artifactKind, downloadName } from './artifact-kind';

interface ArtifactViewerProps {
  artifacts: DemandArtifact[];
  /** Índice do artefato atualmente aberto. */
  index: number;
  onIndexChange: (next: number) => void;
  /** Fecha o viewer (caller volta ao modal preservando a aba). */
  onClose: () => void;
}

export function ArtifactViewer({ artifacts, index, onIndexChange, onClose }: ArtifactViewerProps) {
  const total = artifacts.length;
  const current = artifacts[index];

  // Navegação por teclado: Esc fecha; ← → navega sem fechar.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      else if (e.key === 'ArrowRight' && index < total - 1) onIndexChange(index + 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onClose, onIndexChange]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-raios-bg/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Visualizador de artefato"
      data-testid="artifact-viewer"
    >
      {/* Barra superior: título + navegação + download + fechar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-raios-border shrink-0">
        <span className="text-sm font-medium text-raios-text truncate flex-1" title={current.title}>
          {current.title}
          {current.extension && (
            <span className="ml-2 text-[10px] uppercase text-raios-muted">.{current.extension}</span>
          )}
        </span>

        {total > 1 && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              className="wf-btn wf-btn-ghost p-1 disabled:opacity-40"
              aria-label="Artefato anterior"
              disabled={index === 0}
              onClick={() => onIndexChange(index - 1)}
              data-testid="artifact-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-raios-muted tabular-nums" data-testid="artifact-counter">
              {index + 1} / {total}
            </span>
            <button
              type="button"
              className="wf-btn wf-btn-ghost p-1 disabled:opacity-40"
              aria-label="Próximo artefato"
              disabled={index === total - 1}
              onClick={() => onIndexChange(index + 1)}
              data-testid="artifact-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {current.url && (
          <a
            href={current.url}
            download={downloadName(current)}
            target="_blank"
            rel="noopener noreferrer"
            className="wf-btn wf-btn-ghost p-1 flex items-center gap-1 text-xs"
            aria-label="Baixar artefato"
            data-testid="artifact-download"
          >
            <Download className="h-4 w-4" />
            Baixar
          </a>
        )}

        <button
          type="button"
          className="wf-btn wf-btn-ghost p-1"
          aria-label="Fechar visualizador"
          onClick={onClose}
          data-testid="artifact-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto p-4" data-testid="artifact-content">
        <ArtifactBody key={current.id} artifact={current} />
      </div>
    </div>
  );
}

/** Renderiza o corpo conforme a extensão/mime. */
function ArtifactBody({ artifact }: { artifact: DemandArtifact }) {
  const kind = artifactKind(artifact);
  const url = artifact.url;

  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-full text-raios-muted">
        <FileWarning className="h-8 w-8" />
        <p className="text-sm">Artefato sem URL acessível.</p>
        {artifact.source_ref && (
          <p className="text-[11px] break-all max-w-md text-center">{artifact.source_ref}</p>
        )}
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className="flex items-center justify-center h-full">
        <img
          src={url}
          alt={artifact.title}
          className="max-w-full max-h-full object-contain rounded"
          data-testid="artifact-image"
        />
      </div>
    );
  }

  if (kind === 'pdf' || kind === 'html') {
    return (
      <iframe
        src={url}
        title={artifact.title}
        className="w-full h-full min-h-[70vh] rounded border border-raios-border bg-white"
        data-testid="artifact-iframe"
      />
    );
  }

  if (kind === 'markdown' || kind === 'text' || kind === 'json') {
    return <TextArtifact url={url} kind={kind} />;
  }

  // link — extensão desconhecida: oferece abrir/baixar.
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-full text-raios-muted">
      <p className="text-sm">Pré-visualização indisponível para esta extensão.</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="wf-btn wf-btn-ghost flex items-center gap-1 text-sm"
      >
        <ExternalLink className="h-4 w-4" />
        Abrir em nova aba
      </a>
    </div>
  );
}

/** Busca o texto da signed_url e renderiza (md → MarkdownViewer; json/txt → pre). */
function TextArtifact({ url, kind }: { url: string; kind: 'markdown' | 'text' | 'json' }) {
  const [state, setState] = useState<{ loading: boolean; text: string; error: boolean }>({
    loading: true,
    text: '',
    error: false,
  });

  // ArtifactBody é keyed por current.id (remonta ao trocar de artefato) → o
  // estado inicial { loading:true } já vale; sem reset síncrono no effect
  // (react-hooks/set-state-in-effect).
  useEffect(() => {
    let alive = true;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (alive) setState({ loading: false, text, error: false });
      })
      .catch(() => {
        if (alive) setState({ loading: false, text: '', error: true });
      });
    return () => {
      alive = false;
    };
  }, [url]);

  if (state.loading) {
    return <p className="text-sm text-raios-muted">Carregando conteúdo…</p>;
  }
  if (state.error) {
    return (
      <div className="flex flex-col items-center gap-2 text-raios-muted">
        <FileWarning className="h-6 w-6" />
        <p className="text-sm">Falha ao carregar o conteúdo do artefato.</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-raios-accent-text hover:underline flex items-center gap-1"
        >
          <ExternalLink className="h-3 w-3" />
          Abrir direto
        </a>
      </div>
    );
  }

  if (kind === 'markdown') {
    return <MarkdownViewer content={state.text} data-testid="artifact-markdown" />;
  }

  const pretty =
    kind === 'json'
      ? (() => {
          try {
            return JSON.stringify(JSON.parse(state.text), null, 2);
          } catch {
            return state.text;
          }
        })()
      : state.text;

  return (
    <pre
      className={cn(
        'text-xs text-raios-text whitespace-pre-wrap break-words font-mono',
        'bg-raios-card border border-raios-border rounded p-3',
      )}
      data-testid="artifact-text"
    >
      {pretty}
    </pre>
  );
}
